// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Contador/ContadorClientes.tsx
//  Carteira do escritório: os clientes que liberaram o acesso, com o resumo da
//  competência e o alerta de quem está sem enviar documento.
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import {
  competenciaAtual, contadorService, formatarDoc, rotuloCompetencia, ultimasCompetencias, type ClienteResumo,
} from '@/features/contador/contadorService';

/** Sem receber documento há tantos dias: o cliente pode ter parado de emitir pelo sistema. */
const DIAS_ALERTA = 7;

const diasDesde = (iso?: string | null) =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : null;

export default function ContadorClientes() {
  const navigate = useNavigate();
  const [competencia, setCompetencia] = useState(competenciaAtual());
  const [busca, setBusca] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['contador', 'clientes', competencia],
    queryFn: () => contadorService.clientes(competencia),
  });

  const clientes = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const digitos = termo.replace(/\D/g, '');
    return (data?.clientes ?? []).filter((c) => !termo
      || (c.razaoSocial ?? '').toLowerCase().includes(termo)
      || (digitos.length > 0 && c.cnpjCpf.includes(digitos)));
  }, [data, busca]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Meus clientes</h1>
          <p className="text-muted-foreground mt-1">Empresas que liberaram o acesso aos documentos fiscais</p>
        </div>
        <div className="sm:ml-auto flex gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8 w-full sm:w-56" placeholder="Nome ou CNPJ" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <Select value={String(competencia)} onValueChange={(v) => setCompetencia(Number(v))}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ultimasCompetencias().map((c) => (
                <SelectItem key={c} value={String(c)}>{rotuloCompetencia(c)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : clientes.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {data?.clientes.length
              ? 'Nenhum cliente com esse nome ou CNPJ.'
              : 'Nenhum cliente liberou o acesso ainda. O cliente faz isso no Udisys Gestor, em Plataforma Contador → Acesso do contador.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {clientes.map((c) => <CartaoCliente key={c.contribuinteId} c={c} onAbrir={() =>
            navigate(`/contador/clientes/${c.contribuinteId}?competencia=${competencia}`)} />)}
        </div>
      )}
    </div>
  );
}

function CartaoCliente({ c, onAbrir }: { c: ClienteResumo; onAbrir: () => void }) {
  const dias = diasDesde(c.ultimoRecebido);
  const parado = dias === null || dias >= DIAS_ALERTA;
  return (
    <Card role="button" tabIndex={0} onClick={onAbrir} onKeyDown={(e) => e.key === 'Enter' && onAbrir()}
      className="cursor-pointer transition-colors hover:border-primary">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-start gap-2">
          <Building2 className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0">
            <span className="block truncate">{c.razaoSocial ?? formatarDoc(c.cnpjCpf)}</span>
            <span className="block text-xs font-normal text-muted-foreground">{formatarDoc(c.cnpjCpf)}{c.uf ? ` · ${c.uf}` : ''}</span>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div><p className="text-lg font-semibold">{c.saidas}</p><p className="text-xs text-muted-foreground">Saídas</p></div>
          <div><p className="text-lg font-semibold">{c.entradas}</p><p className="text-xs text-muted-foreground">Entradas</p></div>
          <div><p className="text-lg font-semibold">{c.canceladas}</p><p className="text-xs text-muted-foreground">Canceladas</p></div>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Faturado</span>
          <span className="font-medium">{formatCurrency(c.valorSaidas)}</span>
        </div>
        {parado ? (
          <Badge variant="outline" className="gap-1 border-amber-500 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" />
            {dias === null ? 'Nenhum documento recebido ainda' : `Sem documento novo há ${dias} dias`}
          </Badge>
        ) : (
          <p className="text-xs text-muted-foreground">Último documento recebido há {dias === 0 ? 'menos de 1 dia' : `${dias} dia(s)`}</p>
        )}
      </CardContent>
    </Card>
  );
}
